/**
 * PhotoSwipe base class that can retrieve data about every slide.
 * Shared by PhotoSwipe Core and PhotoSwipe Lightbox
 */
import PhotoSwipe from '../photoswipe'
import Eventable from './eventable';
import {
  getElementsFromOption
} from '../util/util.js';
import Content from '../slide/content';
import type { Point } from '../gestures/gestures';

export type PhotoswipeActionType = 'zoom' | 'zoom-or-close' | 'toggle-controls' | 'next' | 'close' | ((releasePoint: {
  x: number,
  y: number
}, e: Event) => void);

export interface PhotoSwipeOptions {
  allowPanToNext?: boolean,
  spacing?: number,
  loop?: boolean,
  pinchToClose?: boolean,
  closeOnVerticalDrag?: boolean,
  hideAnimationDuration?: number,
  showAnimationDuration?: number,
  zoomAnimationDuration?: number,
  escKey?: boolean,
  arrowKeys?: boolean,
  returnFocus?: boolean,
  maxWidthToAnimate?: number,
  clickToCloseNonZoomable?: boolean,
  imageClickAction?: PhotoswipeActionType,
  bgClickAction?: PhotoswipeActionType,
  tapAction?: PhotoswipeActionType,
  doubleTapAction?: PhotoswipeActionType,
  indexIndicatorSep?: string,
  preloaderDelay?: number,
  bgOpacity?: number,
  errorMsg?: string,
  children?: string,
  childSelector?: string,
  gallery?: string,
  gallerySelector?: string,
  getClickedIndexFn?: (e: Event) => number,
  index?: number,
  pswpModule?: Function,
  openPromise?: () => Promise<any>,
  preloadFirstSlide?: boolean,
  initialPointerPos?: {
    x: number,
    y: number
  },
  dataSource?: {
    length?: number,
    gallery?: HTMLElement,
    items?: HTMLElement[],
  },
  preload?: number[],
  easing?: string,
  mainClass?: string,
  thumbSelector?: string | false,
  getViewportSizeFn?: (options: PhotoSwipeOptions, pswp: PhotoSwipe) => {
    x: number,
    y: number
  },
  paddingFn?: (viewportSize: {
    x: number,
    y: number
  }, itemData: PhotoSwipeItemData, index: number) => {
    [key in 'top' | 'right' | 'bottom' | 'left']: number
  },
  padding?: {
    [key in 'top' | 'right' | 'bottom' | 'left']: number
  },
  showHideAnimationType?: string,
  appendToEl?: HTMLElement,
  showHideOpacity?: boolean,
  wheelToZoom?: boolean,
  closeOnScroll?: boolean
}

export type DataSourceType = Array<{
  src: string,
  w: number,
  h: number
}> | {
  length?: number,
  gallery?: HTMLElement,
  items?: HTMLElement[],
}

export interface PhotoSwipeItemData {
  element?: Element,
  src?: string,
  srcset?: string,
  width?: number,
  height?: number,
  w?: number,
  h?: number,
  type?: string,
  msrc?: string,
  alt?: string,
  thumbCropped?: boolean,
  html?: string
}

class PhotoSwipeBase extends Eventable {
  options: PhotoSwipeOptions;
  viewportSize?: Point

  /**
   * Get total number of slides
   */
  getNumItems() {
    let numItems: number;
    const { dataSource } = this.options;
    if (!dataSource) {
      numItems = 0;
    } else if (dataSource.length) {
      // may be an array or just object with length property
      numItems = dataSource.length;
    } else if (dataSource.gallery) {
      // query DOM elements
      if (!dataSource.items) {
        dataSource.items = this._getGalleryDOMElements(dataSource.gallery);
      }

      if (dataSource.items) {
        numItems = dataSource.items.length;
      }
    }

    // legacy event, before filters were introduced
    const event = this.dispatch('numItems', {
      dataSource,
      numItems
    });
    return this.applyFilters('numItems', event.numItems, dataSource);
  }

  createContentFromData(slideData: PhotoSwipeItemData, index: number) {
    return new Content(slideData, this, index);
  }

  /**
   * Get item data by index.
   *
   * "item data" should contain normalized information that PhotoSwipe needs to generate a slide.
   * For example, it may contain properties like
   * `src`, `srcset`, `w`, `h`, which will be used to generate a slide with image.
   */
  async getItemData(index: number): Promise<PhotoSwipeItemData> {
    const { dataSource } = this.options;
    let dataSourceItem: HTMLElement;
    if (Array.isArray(dataSource)) {
      // Datasource is an array of elements
      dataSourceItem = dataSource[index];
    } else if (dataSource && dataSource.gallery) {
      // dataSource has gallery property,
      // thus it was created by Lightbox, based on
      // gallerySelecor and childSelector options

      // query DOM elements
      if (!dataSource.items) {
        dataSource.items = this._getGalleryDOMElements(dataSource.gallery);
      }

      dataSourceItem = dataSource.items[index];
    }

    let itemData = dataSourceItem;

    if (itemData instanceof Element) {
      // @ts-ignore
      itemData = await this._domElementToItemData(itemData);
    }

    // Dispatching the itemData event,
    // it's a legacy verion before filters were introduced
    const event = this.dispatch('itemData', {
      itemData: itemData || {},
      index
    });

    return this.applyFilters('itemData', event.itemData, index);
  }

  /**
   * Get array of gallery DOM elements,
   * based on childSelector and gallery element.
   */
  _getGalleryDOMElements(galleryElement: HTMLElement) {
    if (this.options.children || this.options.childSelector) {
      return getElementsFromOption(
        this.options.children,
        this.options.childSelector,
        galleryElement
      ) || [];
    }

    return [galleryElement];
  }

  /**
   * Converts DOM element to item data object.
   *
   * @param {Element} element DOM element
   */
  // eslint-disable-next-line class-methods-use-this
  async _domElementToItemData(element: Element): Promise<PhotoSwipeItemData> {
    const itemData: PhotoSwipeItemData = {
      element
    };

    const linkEl: HTMLAnchorElement = element.tagName === 'A' ? element as HTMLAnchorElement : element.querySelector<HTMLAnchorElement>('a');

    if (linkEl) {
      // src comes from data-pswp-src attribute,
      // if it's empty link href is used
      itemData.src = linkEl.dataset.pswpSrc || linkEl.href;

      if (linkEl.dataset.pswpSrcset) {
        itemData.srcset = linkEl.dataset.pswpSrcset;
      }

      itemData.width = parseInt(linkEl.dataset.pswpWidth, 10);
      itemData.height = parseInt(linkEl.dataset.pswpHeight, 10);

      if (!linkEl.dataset.lightboxType) {
        if (isNaN(itemData.width) || isNaN(itemData.height)) {
          await new Promise((resolve, reject) => {
            const img = document.createElement('img');
            img.onload = () => {
              linkEl.dataset.pswpWidth = `${img.naturalWidth}`
              linkEl.dataset.pswpHeight = `${img.naturalHeight}`
              itemData.width = img.naturalWidth;
              itemData.height = img.naturalHeight;
              resolve(null)
            }
            img.onerror = () => {
              reject()
            }
            img.src = itemData.src;
          })
        }
      }

      // support legacy w & h properties
      itemData.w = itemData.width;
      itemData.h = itemData.height;

      if (linkEl.dataset.pswpType) {
        itemData.type = linkEl.dataset.pswpType;
      }

      const thumbnailEl = element.querySelector('img');

      if (thumbnailEl) {
        // msrc is URL to placeholder image that's displayed before large image is loaded
        // by default it's displayed only for the first slide
        itemData.msrc = thumbnailEl.currentSrc || thumbnailEl.src;
        itemData.alt = thumbnailEl.getAttribute('alt');
      }

      if (linkEl.dataset.pswpCropped || linkEl.dataset.cropped) {
        itemData.thumbCropped = true;
      }
    }

    this.applyFilters('domItemData', itemData, element, linkEl);

    return itemData;
  }
}

export default PhotoSwipeBase;
